import React, {useEffect, useState} from 'react'
import {Grid, Stack, Typography} from '@mui/material'
import {blue, Button, DetailedLayout, TextField} from 'finsys-webcomponent'
import moment from 'moment'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {useDispatch} from 'react-redux'
import {Info} from '@mui/icons-material'

import {retrievePageFunctionAccess} from '_helper/_authHelper'
import {dateTimeFormat, HQ_ROLE_ID, METADATA_PAGE_NAME} from '_helper/_constants'
import {setBreadcrumbRecordName} from 'store/actions'
import {Details} from 'components/Metadata/Entity/Details'
import {useEntity} from 'components/Metadata/_hooks/useEntity'
import {useSaveEntity} from 'components/Metadata/_hooks/useSaveEntity'
import {useSnackBar} from 'hoc/SnackbarHandler'
import {getUserRole} from 'localstorage/UserRole'
import {SIDEBAR_DEFAULT, SIDEBAR_INFORMATION} from 'pages/Metadata'

/**
 * Entity Detail
 * @return {JSX.Element}
 * @constructor
 */
const Entity = () => {
  const {setSnackBar} = useSnackBar()
  let {entityId} = useParams()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const [functionAccess, setFunctionAccess] = useState([])
  const [formDetails, setFormDetails] = useState({
    entity_id: null,
    entity_code: null,
    entity_name: null,
    currency: null,
    country_id: null,
    segment_id: null,
  })

  const [sidebarDetails, setSidebarDetails] = useState(SIDEBAR_INFORMATION)
  const {entity} = useEntity(parseInt(entityId))
  const saveEntity = useSaveEntity()

  /**
   * Determine which function does user have access to
   */
  useEffect(() => {
    if (functionAccess.length === 0) {
      retrievePageFunctionAccess(METADATA_PAGE_NAME, setFunctionAccess)
    }

    if (functionAccess === 'FORBIDDEN') {
      navigate('/Forbidden')
    }
  }, [functionAccess])

  /**
   * To validate form
   */
  const validateForm = () => {
    let isGenericFieldsValid = true

    const requiredFields = ['entity_code', 'entity_name', 'currency', 'country_id']

    requiredFields.forEach((field) => {
      if (!formDetails[field] || formDetails[field].length === 0) {
        setFormDetails((prevState) => ({
          ...prevState,
          [`error_${field}`]: 'This is a required field',
        }))

        isGenericFieldsValid = false
      }
    })

    if (!isGenericFieldsValid) {
      setSnackBar({
        open: true,
        type: 'error',
        message: `Missing input! Please fill up all the required input`,
      })
    } else {
      saveEntity.mutate(formDetails)
    }
  }

  /**
   * To initialize data
   */
  useEffect(() => {
    if (!entity) {
      return
    }

    //To set breadcrumb
    dispatch(
      setBreadcrumbRecordName({
        [location.pathname]: `${entity.entity_name}`,
      })
    )

    setFormDetails(entity)
  }, [entity])

  return (
    <DetailedLayout
      mainContent={
        <>
          <Details
            isDisabled={getUserRole().role !== HQ_ROLE_ID}
            errors={saveEntity.errors}
            setFormDetails={setFormDetails}
            formDetails={formDetails}
          />
          {getUserRole().role === HQ_ROLE_ID && (
            <Stack direction="row" spacing={2} sx={{pt: 3, pb: 3}}>
              <Button
                label={'Save'}
                onClick={() => validateForm()}
                disabled={saveEntity.isLoading}
                loading={saveEntity.isLoading}
              />
            </Stack>
          )}
        </>
      }
      sideDrawerContent={
        <Grid container direction={'row'} rowSpacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" align={'left'} sx={{color: '#6B6B6B'}}>
              General
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={'Entity ID'}
              value={formDetails.entity_id}
              disabled={true}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={'Last Updated On'}
              value={moment
                .utc(formDetails.last_update_date)
                .local()
                .format(dateTimeFormat)}
              disabled={true}
            />
          </Grid>
        </Grid>
      }
      collapseSideDrawer={[
        {
          icon: Info,
          label: 'Information',
          iconStyle: {
            color:
              sidebarDetails.type === SIDEBAR_INFORMATION.type
                ? blue.secondary
                : 'inherit',
          },
          onClick: () => setSidebarDetails(SIDEBAR_INFORMATION),
        },
      ]}
      defaultOnToggleSideMenu={(open) => {
        setSidebarDetails(open ? SIDEBAR_DEFAULT : SIDEBAR_INFORMATION)
      }}
    />
  )
}

export default Entity
